/** @file Bones layer: tapered kite per bone (depth colour, selection), outline, then joint dots on top. */
import type { Sink } from '../draw/createSink';
import { emitDisc } from '../draw/emitDisc';
import { emitLine } from '../draw/emitLine';
import { fillSegment } from '../draw/fillSegment';
import { emitQuad } from '../draw/emitQuad';
import { trig } from '@pose2d/math/trig';
import type { OverlayInput } from '../overlayInput';
import { OVERLAY_STYLE } from '../overlayStyle';
import { boneSegment } from './boneSegment';
import { refillSet } from './refillSet';

const seg = new Float64Array(4);
const kite = new Float64Array(8);
const edge = new Float64Array(4);
/** Selected bone indices and hierarchy depth per bone, refilled every call (scratch, no allocation per bone). */
const selected = new Set<number>();
let depth = new Int32Array(64);

/** Outline of the kite's four edges. */
function outline(sink: Sink, width: number): void {
  for (let k = 0; k < 4; k++) {
    const n = (k + 1) % 4;
    emitLine(sink, fillSegment(edge, kite[2 * k] ?? 0, kite[2 * k + 1] ?? 0, kite[2 * n] ?? 0, kite[2 * n + 1] ?? 0), width, OVERLAY_STYLE.boneOutline);
  }
}

/** Kite joint -> shoulder (20 % of the length, at most 12 px) -> tip; bones shorter than 2 px draw nothing. */
function drawKite(sink: Sink, s: Float64Array, fill: number, width: number): void {
  const jx = s[0] ?? 0, jy = s[1] ?? 0, tx = s[2] ?? 0, ty = s[3] ?? 0;
  const len = trig.hypot(tx - jx, ty - jy);
  if (!(len >= 2)) return;
  const ux = (tx - jx) / len, uy = (ty - jy) / len;
  const sh = Math.min(len * 0.2, 12), hw = Math.max(2.5, Math.min(len * 0.08, 7));
  const sx = jx + ux * sh, sy = jy + uy * sh;
  kite[0] = jx;
  kite[1] = jy;
  kite[2] = sx - uy * hw;
  kite[3] = sy + ux * hw;
  kite[4] = tx;
  kite[5] = ty;
  kite[6] = sx + uy * hw;
  kite[7] = sy - ux * hw;
  emitQuad(sink, kite, fill);
  outline(sink, width);
}

/** Fills `depth` for every bone (parents precede children in SkeletonData; an out-of-range parent counts as depth 0). */
function depths(input: OverlayInput): void {
  const bones = input.data.bones, n = bones.length;
  if (depth.length < n) depth = new Int32Array(n);
  depth.fill(0, 0, n);
  for (let i = 0; i < n; i++) {
    const parent = bones[i]?.parent ?? -1;
    depth[i] = parent < 0 ? 0 : (parent < n ? (depth[parent] ?? 0) : 0) + 1;
  }
}

/** The kite of every bone of kind 'bone': depth colour, or yellow with a 2 px outline when selected. */
function kites(sink: Sink, { data, pose, camera }: OverlayInput): void {
  for (let i = 0; i < data.bones.length; i++) {
    const bone = data.bones[i];
    if (bone?.kind !== 'bone') continue;
    const ramp = OVERLAY_STYLE.boneDepth[(depth[i] ?? 0) % OVERLAY_STYLE.boneDepth.length] ?? OVERLAY_STYLE.joint;
    drawKite(sink, boneSegment(pose, camera, i, bone.length, seg), selected.has(i) ? OVERLAY_STYLE.selected : ramp, selected.has(i) ? 2 : 1);
  }
}

/** The joint dot of every bone of kind 'bone', on top of all kites. */
function joints(sink: Sink, { data, pose, camera }: OverlayInput): void {
  for (let i = 0; i < data.bones.length; i++) {
    if (data.bones[i]?.kind !== 'bone') continue;
    boneSegment(pose, camera, i, 0, seg);
    emitDisc(sink, seg[0] ?? 0, seg[1] ?? 0, OVERLAY_STYLE.jointRadius, selected.has(i) ? OVERLAY_STYLE.selected : OVERLAY_STYLE.joint);
  }
}

/** `options.bones`: every bone of kind 'bone' (sockets have their own layer); selected bones in yellow. */
export function drawBones(sink: Sink, input: OverlayInput): void {
  if (input.options.bones !== true) return;
  depths(input);
  refillSet(selected, input.options.selection?.bones);
  kites(sink, input);
  joints(sink, input);
}
